import * as _ from "lodash";
import * as React from "react";
import * as S from "@styled";
import * as AppPath from "../model/AppPath";
import { connect } from "react-redux";
import { DispatchType } from "../../shared/utilities";

import { default as DeployDialog } from "./dialogs/DeployDialog";
import LogLevelSettingsDialog from "./dialogs/LogLevelSettingDialog";

import { default as ListMenu } from "./ListMenu";

import { DraftList } from "./DraftList";
import { ProcessList } from "./ProcessList";

import { HashMap } from "../../shared/model/HashMap";
import { Draft, Process } from "../model/Process";
import { ProcessAggregateCount } from "../model/ProcessAggregateCount";
import { ProcessWithStatus } from "../model/ProcessWithStatus";
import { State } from "../model/State";

import { getDeployingProcessGuids } from "../model/DeployingProcessGuidsSelector";

import * as Actions from "../actions/ProcessListActions";
import * as SelectProcessActions from "../actions/SelectProcessActions";

import { PageLoadSpinner } from "../../shared/components/PageLoadSpinner";
import { TitleAgentGroupSelectMenu } from "../../shared/components/AgentGroupSelectMenu";
import { AgentGroup } from "../../shared/model/AgentGroup";
import { Permissions } from "../../shared/model/AllPermissions";

import { ListState } from "../model/ListState";
import { getAggregateCountsForCurrentAgentGroup, getExecutionFilter } from "../model/ProcessCountSelectors";

import { default as BatchTagDialog } from "./dialogs/BatchTagDialog";
import { default as ConfirmDeleteDialog } from "./dialogs/ConfirmDeleteDialog";
import { ProcessInstanceListWrapper } from "./ProcessInstanceListWrapper";
import { default as RunOnceDialog } from "./dialogs/RunOnceDialog";
import { default as TagDialog } from "./dialogs/TagDialog";
import { default as CleanOldVersionsDialog } from "./dialogs/CleanOldVersionsDialog";

import { IPermissionDictionary } from "../../apiManagement/model/model";

import { getProcessesWithStatus } from "../model/ProcessListSelectors";
import { useQuery } from "shared/hooks/useQueryParams";
import { getNewLink } from "processList/model/AppLinks";
import ProcessChangeLogModal from "processEditor/components/Modals/ProcessChangeLogModal";
import { getRowsPerPage } from "processList/model/RowsPerPage";
import ProcessVariablesEditorModal from "./ProcessVariablesEditorModal";
import { DEFAULT_AGENT_GROUP_NAME } from "processList/actions/DeployActions";
import SelectCreateTemplateFromProcessModeDialog from "./dialogs/template/SelectCreateTemplateFromProcessModeDialog";
import CreateTemplateFromProcessDialog from "./dialogs/template/CreateTemplateFromProcessDialog";

export const ProcessListContainer: React.FC<ProcessListContainerProps> = (props) => {
    const query = useQuery();
    const isDefaultAgentGroup = props.currentAgentGroupName === DEFAULT_AGENT_GROUP_NAME;

    let spinner: JSX.Element | null = null;
    if (props.isLoading) {
        spinner = <PageLoadSpinner />;
    }

    const currentPage = (Number(query.get(AppPath.pageFilterKeyName)) || 1)
    const filterText = query.get("filterText");
    let table: JSX.Element | null = null;

    let totalCountLabel = null;
    if (props.processesWithStatus.length > 0) {
        totalCountLabel = ` ${AppPath.isSubprocessList() ? "Subprocesses" : "Processes"} out of ${props.processCount} ${FRENDS.Localizations.Total.toLowerCase()}`;
    }

    if (props.processesWithStatus) {
        const rowsPerPage = getRowsPerPage();
        const pages = Math.ceil(props.processCount / rowsPerPage);
        table = <ProcessList
            processes={props.processesWithStatus}
            selectedProcesses={props.listState.selectedProcessIds}
            deployingProcessGuids={props.deployingProcessGuids}
            drafts={props.drafts}
            visibleColumns={props.listState.visibleColumns}
            countsTimeLimitDays={props.listState.countsTimeLimitDays}
            onSelectProcesses={props.onSelectProcesses}
            onDeselectProcesses={props.onDeselectProcesses}
            onToggleVisibleColumn={props.onToggleVisibleColumn}
            isSubprocessList={AppPath.isSubprocessList()}
            onShowChangeLogClick={props.onShowChangeLogModal}
            pageCount={pages}
            activePage={currentPage}
            totalCountLabel={totalCountLabel}
            agentGroupName={props.currentAgentGroupName}
        />;
    }

    let draftsTable: JSX.Element | null = null;
    if (props.drafts != null && props.drafts.length > 0) {
        draftsTable = <DraftList drafts={props.drafts} onDiscardDraft={props.onDiscardDraft} />;
    }

    let tagDialog: JSX.Element | null = null;
    if (props.listState.tagModalOpenForProcesses) {
        if (props.listState.tagModalOpenForProcesses.length > 1) {
            tagDialog = <BatchTagDialog processIds={props.listState.tagModalOpenForProcesses} />;
        } else {
            const tagsOpenForProcessId = props.listState.tagModalOpenForProcesses[0];
            const tagsOpenForProcess = props.processesWithStatus.find((p) => p.id === tagsOpenForProcessId);
            if (tagsOpenForProcess) {
                tagDialog = <TagDialog
                    show={true}
                    processId={tagsOpenForProcess.id}
                    currentTags={tagsOpenForProcess.tags}
                />;
            }
        }
    }
    const changeLogModal: JSX.Element | null = React.useMemo(() => {
        if (props.listState.changeLogOpenForProcess) {
            const changeLogProcess = props.processesWithStatus.find((p) => p.id === props.listState.changeLogOpenForProcess);
            return (
                <ProcessChangeLogModal
                    processGuid={changeLogProcess?.uniqueIdentifier}
                    processId={changeLogProcess?.id}
                    processName={changeLogProcess?.name}
                    isSubProcess={AppPath.isSubprocessList()}
                    onHide={props.onHideChangeLogModal}
                />
            );
        }
        return null;
    }, [props.listState.changeLogOpenForProcess]);

    let processVariablesEditorModal: JSX.Element | null = null;
    if (props.listState.processVariablesEditorOpenForProcess) {
        const processBeingProcessVariablesEdited = props.processesWithStatus.find((p) => p.id === props.listState.processVariablesEditorOpenForProcess);
        processVariablesEditorModal = <ProcessVariablesEditorModal process={processBeingProcessVariablesEdited} />
    }

    const handleClearFilters = () => {
        AppPath.replaceSearchParameters(AppPath.getNewUrlState({
            filterCategory: null,
            filterText: null,
            page: 1
        }));
    }

    const handleChangeAgentGroup = (environmentName: string, agentGroupName: string) => {
        AppPath.setAgentGroupToPath(environmentName, agentGroupName);
    }

    return (
        <div>
            {spinner}
            <S.IndentedContainer>
                <S.RelativePageHeaderContainer style={{ minHeight: '83px', justifyContent: 'start' }}>
                    <h1>{AppPath.isSubprocessList() ? FRENDS.Localizations.Subprocesses : FRENDS.Localizations.Processes}</h1>
                    <TitleAgentGroupSelectMenu
                        basePath={`${AppPath.isSubprocessList() ? 'SubProcess' : 'Process'}/List`}
                        agentGroups={_.values(props.agentGroups)}
                        searchQuery={query}
                        isBasePathMatched
                        onChangeAgentGroup={handleChangeAgentGroup}
                    />
                </S.RelativePageHeaderContainer>
                <ListMenu selectedProcesses={props.listState.selectedProcessIds} isSubprocessList={AppPath.isSubprocessList()} />
                {!props.isLoading && _.isEmpty(props.processesWithStatus) && (
                    <S.FlexContainer justifyCenter={true} id="no-processes-container-selector">
                        <S.FormGroup style={{ textAlign: "center" }}>
                            {!_.isEmpty(filterText) ? (
                                <S.FormGroup>
                                    <h3 style={{ margin: '20px 0 12px 0' }}>
                                        No Processes found with filters
                                    </h3>
                                    <S.ClearFilterLink onClick={handleClearFilters} id="clear-filters-selector">{FRENDS.Localizations.ClearFilters}</S.ClearFilterLink>
                                </S.FormGroup>
                            ) : (
                                <S.FormGroup>
                                    <h3 style={{ margin: '20px 0 12px 0' }}>
                                        {isDefaultAgentGroup ? "No Processes" : "No deployed Processes"}
                                    </h3>
                                    {isDefaultAgentGroup ? (
                                        <S.OpenExternalLink variant={S.OpenExternalLinkColorVariant.purple} href={getNewLink()}>Create new Process</S.OpenExternalLink>
                                    ) : (
                                        <S.StyledLink variant={S.LinkTextColor.purple} to={"Default"}>Go to Development Agent group</S.StyledLink>
                                    )}
                                </S.FormGroup>
                            )}
                        </S.FormGroup>
                    </S.FlexContainer>
                )}
                {!_.isEmpty(props.processesWithStatus) && table}
                {draftsTable}
                <DeployDialog />
                <LogLevelSettingsDialog />
                <RunOnceDialog />
                <ConfirmDeleteDialog />
                <CleanOldVersionsDialog />
                <SelectCreateTemplateFromProcessModeDialog />
                <CreateTemplateFromProcessDialog />
                {tagDialog}
                {changeLogModal}
                {processVariablesEditorModal}
            </S.IndentedContainer>
        </div>
    );
};

export const ProcessInstanceListContainerComponent: React.FC<ProcessListContainerProps> = (props) => {

    const query = useQuery();
    const processGuids = query.getAll("processGuids");

    // props.processesWithStatus might not contain the process we want to look at as we only take the first X processes 
    let selectedProcesses: Process[] = [];
    if (props.processesWithStatus !== null && props.processesWithStatus.length !== 0) {
        selectedProcesses = props.processesWithStatus &&
            props.processesWithStatus.filter((proc) => processGuids.some((g) => g === proc.uniqueIdentifier));
    }
    const countsForInstanceProcesses = props.processCountsForCurrentAgentGroup && Object.keys(props.processCountsForCurrentAgentGroup)
        .filter((key) => processGuids.some((guid) => guid === key))
        .reduce((obj: any, key) => {
            obj[key] = props.processCountsForCurrentAgentGroup[key];
            return obj;
        }, {} as ProcessAggregateCount) || {} as ProcessAggregateCount;

    return <ProcessInstanceListWrapper
        agentGroups={props.agentGroups}
        countsForInstanceProcess={countsForInstanceProcesses}
        selectedProcesses={selectedProcesses}
        hasProcessInstanceEditPermission={props.permissions[Permissions.ProcessInstanceEdit]}
        currentAgentGroup={props.currentAgentGroupName}
    />;
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface ProcessListContainerOwnProps { }

export interface ProcessListContainerStateProps {
    processesWithStatus: ProcessWithStatus[];
    processCount: number;
    drafts: Draft[];
    listState: ListState;
    processCountsForCurrentAgentGroup: HashMap<ProcessAggregateCount>;
    deployingProcessGuids: string[];
    isLoading: boolean;
    agentGroups: HashMap<AgentGroup>;
    permissions: IPermissionDictionary;
    environmentVariables: string[];
    currentAgentGroupName: string;
}

export interface ProcessListContainerDispatchProps {
    onSelectProcesses: (processIds: number[]) => void;
    onDeselectProcesses: (processIds: number[]) => void;
    onDiscardDraft: (id: number) => void;
    onToggleVisibleColumn: (column: keyof (ProcessWithStatus)) => void;
    onHideChangeLogModal: () => void;
    onShowChangeLogModal: (processId: number) => void;
}

export type ProcessListContainerProps = ProcessListContainerOwnProps & ProcessListContainerStateProps & ProcessListContainerDispatchProps;

const mapStateToProps = (state: State): ProcessListContainerStateProps => {
    const searchParams = AppPath.getSearchParameters();
    return {
        processesWithStatus: getProcessesWithStatus(state, getExecutionFilter(searchParams.filterCategory, searchParams.filterText as string)),
        drafts: state.drafts,
        listState: state.listState,
        agentGroups: state.agentGroups,
        deployingProcessGuids: getDeployingProcessGuids(state),
        processCountsForCurrentAgentGroup: getAggregateCountsForCurrentAgentGroup(state, getExecutionFilter(searchParams.filterCategory, searchParams.filterText as string)),
        isLoading: (state.loading && state.loading.length > 0),
        permissions: state.userPermissions,
        processCount: state.processCount,
        environmentVariables: state.environmentVariables,
        currentAgentGroupName: state.currentAgentGroupName,
    };
};

const mapDispatchToProps = (dispatch: DispatchType<State>): ProcessListContainerDispatchProps => {
    return {
        onSelectProcesses: (processIds: number[]) => dispatch(SelectProcessActions.selectProcesses(processIds)),
        onDeselectProcesses: (processIds: number[]) => dispatch(SelectProcessActions.deselectProcesses(processIds)),
        onDiscardDraft: (id: number) => dispatch(Actions.discardDraft(id)),
        onToggleVisibleColumn: (column: keyof (ProcessWithStatus)) => dispatch(Actions.toggleVisibleColumn(column)),
        onHideChangeLogModal: () => dispatch(Actions.closeChangeLogModal()),
        onShowChangeLogModal: (processId: number) => dispatch(Actions.openChangeLogModal(processId))
    };
};

export const ProcessInstanceListContainer = connect(mapStateToProps, mapDispatchToProps)(ProcessInstanceListContainerComponent);
export default connect(mapStateToProps, mapDispatchToProps)(ProcessListContainer);
